//import packages
import React, { useState, useEffect } from "react";
import {
  Layout,
  Table,
  Row,
  Col,
  Button,
  Modal,
  message,
  Tabs,
  Tooltip
} from "antd";
import styled from "styled-components";
import {
  DeleteFilled,
  EditOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
} from "@ant-design/icons";

//import components
import UserRolesAndRightsHOComponent from "../../../components/UserRolesAndRightsHOComponent/UserRolesAndRightsHOComponent";
import { StateDetailsModal, StateFormModal } from "./StateModals";

//import other files and API function
import { lookUp } from "../../../api/LookUpAPI";
import { getPharmacies } from "../../../api/PharmacyAPI";
import { addState, deleteState, getAllStates } from "../../../api/StatesAPI";
import authorization from "../../../auth/authorization";
import { BulkUpdate } from "./BulkUpdate";
import { Report } from "./Report";
import {
  StyledComponentWrapper,
  StyledTabWrapper,
  StyledTabs,
  Title,
  TableHeader,
  ActionButtonGroup,
} from "../../SharedStyles";

const { confirm } = Modal;
const { Content } = Layout;

const STATE_NAME_COLUMN_WIDTH = 420;
const CEV_ELIGIBLE_COLUMN_WIDTH = 130;
const ACTION_COLUMN_WIDTH_FOR_VIEW_DETAILS_ONLY = 180;
const ADDITIONAL_ACTION_BUTTON_WIDTH = 48;
const VIEW_DETAILS_ACTION_BUTTON_COUNT = 1;
const TABLE_SCROLL = { x: 860, y: "calc(100vh - 350px)" };

const TableWrapper = styled(StyledComponentWrapper)`
  min-height: calc(100vh - 180px);
`;

const StateComponent = (props) => {
  const { userType } = props;
  const [states, setStates] = useState([]);
  const [allUSStates, setAllUSStates] = useState([]);
  const [telemedicineCategory, setTelemedicineCategory] = useState([]);
  const [editId, setEditId] = useState("");
  const [listOfPharmacies, setListOfPharmacies] = useState([]);
  const [pharmacyLoading, setPharmacyLoading] = useState(false);
  const [viewDetailModal, setViewDetailModal] = useState(false);
  const [stateDetailId, setStateDetailId] = useState(null);
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);

  const canEdit = authorization.isAllowed(userType, "updateState");
  const canDelete = authorization.isAllowed(userType, "deleteState");

  const visibleActionButtonCount = VIEW_DETAILS_ACTION_BUTTON_COUNT + (canEdit ? 1 : 0) + (canDelete ? 1 : 0);
  const additionalActionButtonCount = visibleActionButtonCount - VIEW_DETAILS_ACTION_BUTTON_COUNT;
  const actionColumnWidth = ACTION_COLUMN_WIDTH_FOR_VIEW_DETAILS_ONLY + additionalActionButtonCount * ADDITIONAL_ACTION_BUTTON_WIDTH;

  const columns = [
    { title: "Name", dataIndex: "name", key: "name", width: STATE_NAME_COLUMN_WIDTH, ellipsis: true },
    {
      title: "CEV Eligible",
      dataIndex: "oneClickPrescribeEligible",
      key: "oneClickPrescribeEligible",
      width: CEV_ELIGIBLE_COLUMN_WIDTH,
      align: "center",
      render: (eligible) => (eligible ? "✅" : "🚫"),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: actionColumnWidth,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <ActionButtonGroup>
          <Tooltip title="View details">
            <Button
              shape="circle"
              icon={<FileTextOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                singleStateView(record._id);
              }}
            />
          </Tooltip>
          {canEdit && (
            <Tooltip title="Edit">
              <Button
                shape="circle"
                icon={<EditOutlined />}
                onClick={(event) => {
                  event.stopPropagation();
                  onStateEdit(record.key);
                }}
              />
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Delete">
              <Button
                shape="circle"
                icon={<DeleteFilled />}
                onClick={(event) => {
                  event.stopPropagation();
                  showDeleteConfirmation(record, record.key);
                }}
              />
            </Tooltip>
          )}
        </ActionButtonGroup>
      ),
    },
  ];

  const consultationType = [
    {
      key: "store-and-forward",
      name: "STORE AND FORWARD",
      value: "store and forward",
    },
    { key: "audio", name: "AUDIO", value: "audio" },
    { key: "video", name: "VIDEO", value: "video" },
  ];

  const fetchPharmacies = (name) => {
    if (name) {
      setListOfPharmacies([]);
      setPharmacyLoading(true);

      getPharmacies(name)
        .then(({ data: { error, pharmacies } }) => {
            if (error || !pharmacies) return message.error("Unable to fetch Pharmacy List. Please try again!");
    
            const fetchPharmaciesList = pharmacies.map((eachPharmacy) => ({
              ...eachPharmacy,
              name: `${eachPharmacy.name} (${eachPharmacy.state})`,
              key: eachPharmacy.pharmacyId,
              value: eachPharmacy.pharmacyId,
              state: eachPharmacy.state,
            }));

            setListOfPharmacies(fetchPharmaciesList);
            message.success("Successfully fetched Pharmacy List");
        })
        .catch(({ response }) => {
          const errorMessage = "Error occurred while fetching Pharmacy List. Please try again!"
          message.error(response?.data?.message || errorMessage)
        })
        .finally(() => setPharmacyLoading(false))
    }
  };

  const fetchStates = () => {
    lookUp("state").then((res) => {
      if (res.data && res.data.payload) {
        const stateData = res.data.payload.map((eachState) => ({
          ...eachState,
          key: eachState._id,
        }));
        setStates(stateData);
      }
    });
  };

  const fetchAllUSStates = () => {
    getAllStates("").then((res) => {
      if (res.data && res.data.payload) {
        const allStateData = res.data.payload.map((eachState) => ({
          name: eachState,
          key: `key-${eachState}`,
        }));
        setAllUSStates(allStateData);
      }
    });
  };
  const fetchTelecategory = () => {
    lookUp("tele-medicine-category").then((res) => {
      if (res.data && res.data.payload) {
        const telemedicineData = res.data.payload.map(
          (eachMedicineCategory) => ({
            ...eachMedicineCategory,
            key: eachMedicineCategory._id,
          })
        );
        setTelemedicineCategory(telemedicineData);
      }
    });
  };

  useEffect(() => {
    fetchStates();
    fetchTelecategory();
  }, []);

  useEffect(() => {
    if (userType && authorization.isAllowed(userType, "searchState")) {
      fetchAllUSStates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]);

  const onAddingState = async (stateDetails) => {
    try {
      const { data } = await addState(stateDetails);
      if (!data?.success) throw new Error(data.message || "Unable to add State. Please try again!");
      fetchStates();
      closeFormModal();
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message)
    }
  }

  const onDeletingState = (stateId) => {
    deleteState(stateId)
      .then((res) => {
        const { status } = res;
        if (status && status === 204) {
          message.success("Successfully Deleted State");
          closeFormModal();
        } else {
          const { data } = res;
          if (data && data.success) {
            message.success("Successfully Deleted State");
            closeFormModal();
          } else {
            if (data && data.message) {
              message.error(data.message);
            } else {
              message.error("Unable to delete State. Please try again!");
            }
          }
        }
        fetchStates();
      })
      .catch((error) => {
        console.log(
          "================== delete state error ========================",
          error
        );
        const { response } = error;
        if (response) {
          const { data } = response;
          if (data && data.message) {
            message.error(data.message);
          } else {
            message.error("Unable to delete state. Please try again!");
          }
        } else {
          message.error("Unable to delete state. Please try again!");
        }
      });
  };

  const showDeleteConfirmation = (state, stateId) => {
    confirm({
      title: `Do you want to delete ${state.name} state?`,
      icon: <ExclamationCircleOutlined />,
      onOk: () => {
        onDeletingState(stateId);
      },
      onCancel() {},
    });
  };

  const onStateEdit = (stateId) => {
    setEditId(stateId);
    setIsFormModalVisible(true);
  };

  const onStateAdd = () => {
    setEditId("");
    setIsFormModalVisible(true);
  };

  const closeFormModal = () => {
    setEditId("");
    setIsFormModalVisible(false);
  };

  const singleStateView = (id) => {
    setStateDetailId(id);
    setViewDetailModal(true);
  };

  return (
    <>
      <Content>
        <StyledTabWrapper>
        <StyledTabs
          defaultActiveKey="add-edit"
          type='card'
          items={[{
            key: 'add-edit',
            label: 'Add/Edit States',
            children: <>
              <StateFormModal
                open={isFormModalVisible}
                editId={editId}
                userType={userType}
                onCancel={closeFormModal}
                addState={onAddingState}
                afterStateEdit={closeFormModal}
                fetchStatesAfterUpdate={fetchStates}
                states={allUSStates}
                telemedicineCategory={telemedicineCategory}
                consultationType={consultationType}
                listOfPharmacies={listOfPharmacies}
                fetchPharmacies={fetchPharmacies}
                pharmacyLoading={pharmacyLoading}
              />
              <TableWrapper>
                <TableHeader>
                  <Title level={2}>States</Title>
                  {authorization.isAllowed(userType, "createState") ? (
                    <Button type="primary" size="large" onClick={onStateAdd}>
                      Add
                    </Button>
                  ) : null}
                </TableHeader>
                <Row>
                  <Col span={24}>
                    <Table
                      columns={columns}
                      dataSource={states}
                      pagination={false}
                      scroll={TABLE_SCROLL}
                      tableLayout="fixed"
                    />
                  </Col>
                </Row>
              </TableWrapper>
              <StateDetailsModal
                open={viewDetailModal}
                stateDetailId={stateDetailId}
                onCancel={() => setViewDetailModal(false)}
              />
            </>
          }, {
            key: 'bulk-update',
            label: 'Bulk Update',
            children: <StyledComponentWrapper>
              <BulkUpdate states={states} fetchStates={fetchStates} categories={telemedicineCategory}/>
            </StyledComponentWrapper>
          }, {
            key: 'report',
            label: 'Report',
            children: <StyledComponentWrapper>
              <Report states={states} categories={telemedicineCategory} />
            </StyledComponentWrapper>
          }]}
        />

        </StyledTabWrapper>
      </Content>
    </>
  );
};

export default UserRolesAndRightsHOComponent(StateComponent);
